import React, { useState, useEffect } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Calendar, Clock, MapPin, Users, Mail, Phone, Tag,
  PartyPopper, X, Facebook, Share2, Linkedin, Twitter, MessageCircle, Link2, Send, Check
} from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventsPage() {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/events`);
        setEvents(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetchEvents();
  }, []);

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      const locale = i18n.language?.startsWith('nl') ? 'nl-BE' : i18n.language?.startsWith('en') ? 'en-US' : 'fr-BE';
      return d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return dateStr; }
  };

  const formatDateShort = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      const locale = i18n.language?.startsWith('nl') ? 'nl-BE' : i18n.language?.startsWith('en') ? 'en-US' : 'fr-BE';
      return { day: d.getDate(), month: d.toLocaleDateString(locale, { month: 'short' }).toUpperCase() };
    } catch { return { day: '', month: '' }; }
  };

  const isPast = (dateStr) => {
    try { return new Date(dateStr + 'T23:59:59') < new Date(); }
    catch { return false; }
  };

  const filtered = events;

  // Split upcoming vs past + sort chronologically.
  const upcoming = filtered
    .filter(e => !isPast(e.date))
    .sort((a, b) => a.date.localeCompare(b.date));
  const past = filtered
    .filter(e => isPast(e.date))
    .sort((a, b) => b.date.localeCompare(a.date));

  const countLabel = (n) => `${n}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1A3C34] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Reusable card renderer — used in both "upcoming" and "past" sections
  const renderCard = (evt) => {
    const dateShort = formatDateShort(evt.date);
    const past = isPast(evt.date);
    return (
      <div
        key={evt.event_id}
        data-testid={`event-card-${evt.event_id}`}
        className={`bg-white border border-[#E2E2DF] rounded-xl overflow-hidden group cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative flex flex-col ${past ? 'opacity-75' : ''}`}
        onClick={() => setSelectedEvent(evt)}
      >
        {past && (
          <div className="absolute top-0 left-0 w-full h-full z-10 pointer-events-none overflow-hidden" data-testid={`event-ended-banner-${evt.event_id}`}>
            <div className="absolute -left-10 top-5 w-40 bg-[#1A3C34] text-white text-[10px] font-bold uppercase tracking-widest text-center py-1 -rotate-45 shadow-md">
              {t('events.ended')}
            </div>
          </div>
        )}
        {evt.is_full && !past && (
          <div className="absolute top-0 left-0 w-full h-full z-10 pointer-events-none overflow-hidden">
            <div className="absolute -left-10 top-5 w-40 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest text-center py-1 -rotate-45 shadow-md">
              {t('events.full')}
            </div>
          </div>
        )}
        <div className="relative aspect-[16/9] overflow-hidden bg-[#F0EFEA] flex-shrink-0">
          {evt.image_url ? (
            <img
              src={evt.image_url}
              alt={evt.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #C77D6322 0%, #C77D6355 100%)' }}
            >
              <Calendar className="w-14 h-14 text-[#C77D63]" />
            </div>
          )}
          {/* Floating date pill top-left */}
          <div className="absolute top-3 left-3 bg-white/95 backdrop-blur rounded-lg px-2.5 py-1.5 shadow-md flex flex-col items-center min-w-[46px]">
            <span className="text-lg font-bold leading-none text-[#C77D63]">{dateShort.day}</span>
            <span className="text-[9px] font-bold uppercase tracking-wider leading-none mt-0.5 text-[#C77D63]">{dateShort.month}</span>
          </div>
        </div>
        <div className="p-4 flex-1 flex flex-col">
          <h3 className="font-heading text-base font-bold text-[#1A3C34] line-clamp-2 group-hover:text-[#C77D63] transition-colors">
            {evt.title}
          </h3>
          <p className="mt-2 text-xs text-[#737373] line-clamp-2 leading-relaxed">{evt.description}</p>
          <div className="mt-auto pt-3 flex items-center gap-3 text-[11px] text-[#737373] border-t border-[#F0EFEA]">
            <span className="flex items-center gap-1 font-semibold text-[#1A3C34]">
              <Clock className="w-3 h-3" /> {evt.start_time}—{evt.end_time}
            </span>
            {evt.organizer && (
              <span className="flex items-center gap-1 truncate">
                <Users className="w-3 h-3" /> {evt.organizer}
              </span>
            )}
            <span className={`ml-auto font-bold ${evt.price > 0 ? 'text-[#C77D63]' : 'text-emerald-700'}`}>
              {evt.price > 0
                ? `${evt.price} EUR`
                : t('events.free', { defaultValue: 'Gratuit' })}
            </span>
          </div>
          {(evt.contact_phone || evt.contact_email || evt.facebook_url) && (
            <div
              className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#737373]"
              data-testid={`event-contact-row-${evt.event_id}`}
              onClick={(e) => e.stopPropagation()}
            >
              {evt.contact_phone && (
                <a
                  href={`tel:${evt.contact_phone.replace(/\s+/g, '')}`}
                  className="flex items-center gap-1 hover:text-[#C77D63] transition-colors"
                >
                  <Phone className="w-3 h-3" /> {evt.contact_phone}
                </a>
              )}
              {evt.contact_email && (
                <a
                  href={`mailto:${evt.contact_email}`}
                  className="flex items-center gap-1 truncate max-w-full hover:text-[#C77D63] transition-colors"
                >
                  <Mail className="w-3 h-3" /> <span className="truncate">{evt.contact_email}</span>
                </a>
              )}
              {evt.facebook_url && (
                <a
                  href={evt.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-[#C77D63] transition-colors"
                >
                  <Facebook className="w-3 h-3" /> Facebook
                </a>
              )}
            </div>
          )}
          {/* Register + Share buttons */}
          <div
            className="mt-3 flex items-center gap-2"
            data-testid={`event-actions-${evt.event_id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <RegisterButton event={evt} />
            <ShareMenu event={evt} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="events-page">
      <SEO titleKey="seo.events.title" descriptionKey="seo.events.description" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C77D63]/10 border border-[#C77D63]/20 rounded-full text-sm font-semibold text-[#C77D63] mb-6">
            <PartyPopper className="w-4 h-4" /> {t('events.discoverWhats')}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#1A3C34] tracking-tight">
            {t('events.title')}
          </h1>
          <p className="mt-4 text-base text-[#737373] max-w-lg mx-auto leading-relaxed font-body">
            {t('events.subtitle')}
          </p>
        </div>

        {/* Upcoming section */}
        <section className="mb-14" data-testid="events-upcoming-section">
          <div className="flex items-baseline gap-3 mb-5">
            <h2 className="font-heading text-2xl font-bold text-[#1A3C34]">
              {t('events.upcoming', { defaultValue: 'À venir' })}
            </h2>
            <span className="text-sm font-bold text-[#C77D63]">
              {countLabel(upcoming.length)}
            </span>
          </div>
          {upcoming.length === 0 ? (
            <div className="text-center py-16 bg-white border border-dashed border-[#E2E2DF] rounded-xl" data-testid="events-empty-upcoming">
              <Calendar className="w-10 h-10 text-[#E2E2DF] mx-auto mb-3" />
              <p className="text-sm text-[#737373]">
                {t('events.noUpcoming', { defaultValue: 'Aucun événement à venir pour le moment. Revenez bientôt !' })}
              </p>
            </div>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
              data-testid="events-grid"
            >
              {upcoming.map(renderCard)}
            </div>
          )}
        </section>

        {/* Past section — collapsed by default */}
        {past.length > 0 && (
          <section data-testid="events-past-section">
            <div className="flex items-baseline justify-between gap-3 mb-5">
              <div className="flex items-baseline gap-3">
                <h2 className="font-heading text-2xl font-bold text-[#1A3C34]/70">
                  {t('events.pastEvents', { defaultValue: 'Événements passés' })}
                </h2>
                <span className="text-sm font-bold text-[#737373]">
                  {countLabel(past.length)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowPast(v => !v)}
                data-testid="events-past-toggle"
                className="text-xs font-bold text-[#C77D63] hover:text-[#b06a51] uppercase tracking-wider"
              >
                {showPast
                  ? t('events.hidePast', { defaultValue: 'Masquer' })
                  : t('events.showPast', { defaultValue: 'Afficher' })}
              </button>
            </div>
            {showPast && (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                data-testid="events-past-grid"
              >
                {past.map(renderCard)}
              </div>
            )}
          </section>
        )}

        {/* CTA */}
        <div className="mt-16 text-center bg-white border border-[#E2E2DF] rounded-xl p-8 shadow-sm">
          <h2 className="font-heading text-xl font-bold text-[#1A3C34]">
            {t('events.ctaTitle')}
          </h2>
          <p className="mt-2 text-sm text-[#737373] max-w-md mx-auto">
            {t('events.ctaDescription')}
          </p>
          <a href="mailto:info@flexi-location.be" className="inline-flex items-center gap-2 mt-4 px-6 py-2.5 bg-[#1A3C34] text-white rounded-md text-sm font-bold hover:bg-[#1A3C34]/90 transition-colors">
            <Mail className="w-4 h-4" /> {t('events.ctaContact')}
          </a>
        </div>
      </div>

      {/* Event detail modal */}
      {selectedEvent && (
        <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </div>
  );
}

function EventDetailModal({ event, onClose }) {
  const { t, i18n } = useTranslation();
  const [showFullImg, setShowFullImg] = useState(false);
  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      const locale = i18n.language?.startsWith('nl') ? 'nl-BE' : i18n.language?.startsWith('en') ? 'en-US' : 'fr-BE';
      return d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="event-modal-backdrop"
    >
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="event-detail-modal"
      >
        {/* Close (X) — always visible top-right, works on any background */}
        <button
          type="button"
          onClick={onClose}
          data-testid="event-modal-close"
          aria-label={t('events.close')}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/95 hover:bg-white text-[#1A3C34] flex items-center justify-center shadow-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        {/* Hero image — 16:9 crop for the modal ONLY (never any black band
            regardless of source aspect). The full uncropped image is one click
            away via the lightbox below. */}
        {event.image_url && (
          <div
            className="cursor-pointer relative group overflow-hidden aspect-video bg-[#F0EFEA] flex-shrink-0"
            onClick={() => setShowFullImg(true)}
          >
            <img
              src={event.image_url}
              alt={event.title}
              className="block w-full h-full object-cover object-center group-hover:scale-[1.02] transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center pointer-events-none">
              <span className="text-white text-xs bg-black/50 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">{t('events.clickToZoom')}</span>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="p-6 pb-0">
          <h2 className="font-heading text-xl font-bold text-[#1A3C34] pr-8">{event.title}</h2>
          {event.organizer && (
            <p className="text-sm text-[#C77D63] mt-1 font-semibold">{t('events.byOrganizer')} {event.organizer}</p>
          )}
          <div className="flex items-center gap-2 mt-3">
            {event.price > 0 ? (
              <Badge className="bg-[#C77D63] text-white text-xs font-bold px-2.5 py-1">
                {event.price} EUR
              </Badge>
            ) : (
              <Badge className="bg-emerald-600 text-white text-xs font-bold px-2.5 py-1">
                {t('events.free', { defaultValue: 'Gratuit' })}
              </Badge>
            )}
            {event.is_full && (
              <Badge className="bg-red-600 text-white text-xs font-bold">{t('events.full')}</Badge>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[55vh] space-y-5">
          <p className="text-sm text-[#737373] leading-relaxed">{event.description}</p>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2.5 bg-[#F9F9F7] rounded-md p-3">
              <Calendar className="w-4 h-4 text-[#1A3C34]" />
              <div>
                <p className="text-[10px] text-[#737373]">{t('events.modalDate')}</p>
                <p className="text-xs font-semibold text-[#1A3C34] capitalize">{formatDate(event.date)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 bg-[#F9F9F7] rounded-md p-3">
              <Clock className="w-4 h-4 text-[#1A3C34]" />
              <div>
                <p className="text-[10px] text-[#737373]">{t('events.modalSchedule')}</p>
                <p className="text-xs font-semibold text-[#1A3C34]">{event.start_time} - {event.end_time}</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 bg-[#F9F9F7] rounded-md p-3">
              <MapPin className="w-4 h-4 text-[#1A3C34]" />
              <div>
                <p className="text-[10px] text-[#737373]">{t('events.modalLocation')}</p>
                <p className="text-xs font-semibold text-[#1A3C34]">{event.location}</p>
              </div>
            </div>
            {event.max_participants && (
              <div className="flex items-center gap-2.5 bg-[#F9F9F7] rounded-md p-3">
                <Users className="w-4 h-4 text-[#1A3C34]" />
                <div>
                  <p className="text-[10px] text-[#737373]">{t('events.modalSeats')}</p>
                  <p className="text-xs font-semibold text-[#1A3C34]">{t('events.modalSeatsMax', { count: event.max_participants })}</p>
                </div>
              </div>
            )}
          </div>

          {/* Tags */}
          {event.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {event.tags.map((tag, i) => (
                <Badge key={i} className="bg-[#F0EFEA] text-[#737373] text-[10px] px-2 py-0.5 font-normal">
                  <Tag className="w-2.5 h-2.5 mr-1" /> {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#E2E2DF] p-4 flex gap-2 flex-wrap">
          <RegisterButton event={event} compact />
          {event.contact_email && (
            <a
              href={`mailto:${event.contact_email}?subject=${t('events.modalRegisterSubject')}: ${event.title}`}
              className="flex items-center justify-center gap-2 h-10 px-4 border border-[#E2E2DF] rounded-md text-sm font-medium text-[#1A3C34] hover:bg-[#F0EFEA] transition-colors"
              data-testid="event-contact-btn"
              aria-label={t('events.modalContactOrg')}
            >
              <Mail className="w-4 h-4" />
            </a>
          )}
          {event.contact_phone && (
            <a
              href={`tel:${event.contact_phone}`}
              className="flex items-center justify-center gap-2 h-10 px-4 border border-[#E2E2DF] rounded-md text-sm font-medium text-[#1A3C34] hover:bg-[#F0EFEA] transition-colors"
              aria-label="Téléphone"
            >
              <Phone className="w-4 h-4" />
            </a>
          )}
          {event.facebook_url && (
            <a
              href={event.facebook_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 h-10 px-4 border border-[#E2E2DF] rounded-md text-sm font-medium text-[#1A3C34] hover:bg-[#F0EFEA] transition-colors"
              data-testid="event-facebook-btn"
              aria-label="Facebook"
            >
              <Facebook className="w-4 h-4" />
            </a>
          )}
          <ShareMenu event={event} compact />
          <button
            onClick={onClose}
            className="flex items-center justify-center h-10 px-4 border border-[#E2E2DF] rounded-md text-sm text-[#737373] hover:bg-[#F0EFEA] transition-colors"
          >
            {t('events.close')}
          </button>
        </div>
      </div>
    </div>
    {/* Lightbox */}
    {showFullImg && event.image_url && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 px-4" onClick={() => setShowFullImg(false)}>
        <div className="relative max-w-4xl max-h-[90vh]">
          <img src={event.image_url} alt={event.title} className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" />
          <button onClick={() => setShowFullImg(false)} className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-[#737373] hover:text-[#1A3C34]">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    )}
    </>
  );
}

// -------------------------------------------------------------------------
// ShareMenu — inline popover with social share links + native Web Share API.
// Kept intentionally small (no external dep). Uses the current page URL with
// an `event={id}` hash so the shared link deep-links to the right modal.
// -------------------------------------------------------------------------
function ShareMenu({ event, compact = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  // Prefer the site's public domain when in production; fall back to current origin
  const origin = (typeof window !== 'undefined' && window.location.origin) || '';
  const isProdHost = /flexi-location\.be$/i.test(origin);
  const baseHost = isProdHost ? origin : 'https://flexi-location.be';
  // Server-rendered share URL: Facebook / WhatsApp / LinkedIn scrapers get proper
  // OG tags (title/description/image); humans get redirected to the SPA modal.
  const shareUrl = `${baseHost}/api/events/${event.event_id}/share`;
  const shareText = `${event.title} — ${event.date}`;
  const encU = encodeURIComponent(shareUrl);
  const encT = encodeURIComponent(shareText);

  const trigger = async (e) => {
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({ title: event.title, text: shareText, url: shareUrl });
        return;
      } catch { /* user cancelled — fall through to menu */ }
    }
    setOpen(o => !o);
  };

  const copyLink = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t('events.linkCopied', { defaultValue: 'Lien copié' }));
    } catch {
      toast.error('Copie impossible');
    }
    setOpen(false);
  };

  const links = [
    { key: 'fb', label: 'Facebook', icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encU}` },
    { key: 'x',  label: 'X',        icon: Twitter,  href: `https://twitter.com/intent/tweet?url=${encU}&text=${encT}` },
    { key: 'li', label: 'LinkedIn', icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encU}` },
    { key: 'wa', label: 'WhatsApp', icon: MessageCircle, href: `https://wa.me/?text=${encT}%20${encU}` },
    { key: 'tg', label: 'Telegram', icon: Send,     href: `https://t.me/share/url?url=${encU}&text=${encT}` },
    { key: 'em', label: 'Email',    icon: Mail,     href: `mailto:?subject=${encT}&body=${encU}` },
  ];

  return (
    <div ref={ref} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={trigger}
        data-testid={`event-share-btn-${event.event_id}`}
        aria-label={t('events.share', { defaultValue: 'Partager' })}
        className={
          compact
            ? 'flex items-center justify-center h-10 px-4 border border-[#E2E2DF] rounded-md text-sm font-medium text-[#1A3C34] hover:bg-[#F0EFEA] transition-colors'
            : 'flex items-center justify-center gap-1.5 h-9 px-3 border border-[#E2E2DF] rounded-md text-xs font-medium text-[#1A3C34] hover:bg-[#F0EFEA] transition-colors'
        }
      >
        <Share2 className="w-4 h-4" />
        {!compact && <span>{t('events.share', { defaultValue: 'Partager' })}</span>}
      </button>
      {open && (
        <div
          className="absolute right-0 bottom-full mb-2 z-30 w-52 bg-white border border-[#E2E2DF] rounded-lg shadow-xl p-1.5"
          data-testid={`event-share-menu-${event.event_id}`}
        >
          {links.map(l => (
            <a
              key={l.key}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-2.5 py-2 text-xs text-[#1A3C34] hover:bg-[#F0EFEA] rounded-md transition-colors"
            >
              <l.icon className="w-3.5 h-3.5 text-[#C77D63]" /> {l.label}
            </a>
          ))}
          <button
            type="button"
            onClick={copyLink}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs text-[#1A3C34] hover:bg-[#F0EFEA] rounded-md transition-colors"
          >
            <Link2 className="w-3.5 h-3.5 text-[#C77D63]" /> {t('events.copyLink', { defaultValue: 'Copier le lien' })}
          </button>
        </div>
      )}
    </div>
  );
}

// Register button — reuses the best available contact channel (email preferred,
// then Facebook event page, then phone). Returns `null` if none is set.
function RegisterButton({ event, compact = false }) {
  const { t } = useTranslation();
  const subject = t('events.modalRegisterSubject', { defaultValue: 'Inscription' });
  let href = null;
  if (event.contact_email) {
    href = `mailto:${event.contact_email}?subject=${encodeURIComponent(`${subject}: ${event.title}`)}`;
  } else if (event.facebook_url) {
    href = event.facebook_url;
  } else if (event.contact_phone) {
    href = `tel:${event.contact_phone.replace(/\s+/g, '')}`;
  }
  if (!href) return null;
  const isExternal = href.startsWith('http');
  return (
    <a
      href={href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      onClick={(e) => e.stopPropagation()}
      data-testid={`event-register-btn-${event.event_id}`}
      className={
        compact
          ? 'flex-1 flex items-center justify-center gap-2 h-10 bg-[#C77D63] text-white rounded-md text-sm font-bold hover:bg-[#b06a51] transition-colors'
          : 'flex-1 flex items-center justify-center gap-1.5 h-9 px-3 bg-[#C77D63] text-white rounded-md text-xs font-bold hover:bg-[#b06a51] transition-colors'
      }
    >
      <Check className="w-4 h-4" /> {t('events.register', { defaultValue: "S'inscrire" })}
    </a>
  );
}

